import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Navbar, Icon, Button, Card, CarInfoBox, ReviewButton, PageHeader, SectionHeader,
  TextBox, Badge, RouteEditSection, SurchargeNotice,
  StickyBottomBar, PriceDisplay, ErrorBoundary,
} from '../../../components/common';
import { RESERVATION_STEPS, TOTAL_RESERVATION_STEPS } from '../../../constants/reservationSteps';
import ReviewListModal from '../../../components/service/ReviewListModal';
import RouteEditModal from '../../../components/service/RouteEditModal';
import BookerInfoCard from '../../../components/service/BookerInfoCard';
import ServiceOptionList, { useServiceOptionList } from '../../../components/service/ServiceOptionList';
import { tourService, pricingService, reviewService } from '../../../api/services';
import { useMoney } from '../../../hooks/useMoney';
import { useAvailabilityCheck } from '../../../hooks/useAvailabilityCheck';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';
import { useAuth } from '../../../contexts/AuthContext';

export default function TourBookingInfo() {
  const { t } = useTranslation();
  const money = useMoney();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stateTour = location?.state?.tour;

  // 결제 페이지로 갔다가 뒤로가기 시 폼 입력값이 휘발되던 문제 — history entry 별
  // location.key 로 sessionStorage 에 draft 저장/복원. 새 진입(다른 key)은 fresh.
  const draftKey = `vvt_tourbooking_draft_${location.key || 'default'}`;

  const { check: checkAvailability, modal: availabilityModal } = useAvailabilityCheck({
    serviceType: 'tour',
    dateTime: location?.state?.dateTime || '',
    passengers: location?.state?.peopleQty || 1,
    onSelectOtherDate: () => navigate(-1),
  });

  /* 마운트 시 가용성 체크 */
  useEffect(() => {
    if (location?.state?.dateTime) checkAvailability();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tour, setTour] = useState({
    title: stateTour?.name || '',
    dateTime: location?.state?.dateTime || '',
    vehicle: '',
    basePrice: Number(stateTour?.price) || 0,
    surcharge: '',
    surchargeOvertimePrice: 0,
    baseHours: stateTour?.duration_hours || 2,
    itinerary: stateTour?.itinerary || [],
    recommendedCourses: stateTour?.recommended_courses || [],
    // #166: 가격 자동 합산용
    minPassengers: Number(stateTour?.min_passengers) || 1,
    maxPassengers: Number(stateTour?.max_passengers) || 6,
    extraPassengerPrice: Number(stateTour?.extra_passenger_price) || 0,
    weekendSurcharge: Number(stateTour?.weekend_surcharge) || 0,
    nightSurcharge: Number(stateTour?.night_surcharge) || 0,
    nightStartTime: stateTour?.night_start_time || '18:00',
    nightEndTime: stateTour?.night_end_time || '06:00',
  });
  const [vehicleSpecs, setVehicleSpecs] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const [tourData, vehicleData, surchargeData] = await Promise.all([
          stateTour ? Promise.resolve(stateTour) : tourService.getById(id),
          pricingService.getVehicleInfo('tour'),
          pricingService.getServiceSurcharges('tour'),
        ]);
        // #166/#207: 투어는 어드민에서 투어별 max 설정. 문구는 "탑승 인원: 최대 N명" 통일.
        const _tourMax = Number(tourData.max_passengers) || vehicleData.max_passengers || 6;
        setVehicleSpecs({
          ...vehicleData,
          capacity: _tourMax ? `탑승 인원: 최대 ${_tourMax}명` : (vehicleData.capacity || ''),
        });
        setTour({
          title: tourData.name || '',
          dateTime: location?.state?.dateTime || '',
          vehicle: vehicleData.carName || '',
          // #166: BE Decimal 응답이 문자열 ("199000")로 와서 그대로 두면 + 연산이 문자열
          // concat 됨("199000"+90000 → "19900090000"). Number() 로 강제 변환.
          basePrice: Number(tourData.price) || 0,
          surcharge: surchargeData.overtime || '',
          surchargeOvertimePrice: Number(surchargeData.overtime_price) || 0,
          baseHours: tourData.duration_hours || 2,
          itinerary: tourData.itinerary || [],
          recommendedCourses: tourData.recommended_courses || [],
          // #166
          minPassengers: Number(tourData.min_passengers) || 1,
          maxPassengers: Number(tourData.max_passengers) || 6,
          extraPassengerPrice: Number(tourData.extra_passenger_price) || 0,
          weekendSurcharge: Number(tourData.weekend_surcharge) || 0,
          nightSurcharge: Number(tourData.night_surcharge) || 0,
          nightStartTime: tourData.night_start_time || '18:00',
          nightEndTime: tourData.night_end_time || '06:00',
        });
      } catch (err) {
        console.warn('TourBookingInfo 데이터 로드 실패:', err.message);
      }
    };
    loadData();
  }, [id, stateTour]);

  // 어드민이 입력한 itinerary (JSONB list of {time, icon, title, desc, address}) 우선 사용.
  // 없으면 legacy recommended_courses (string array) fallback. TourDetail 와 동일 패턴.
  const recommendedCoursesBundle = useMemo(
    () => {
      const itin = tour.itinerary;
      if (Array.isArray(itin) && itin.length > 0) {
        return itin.map((step) => {
          if (typeof step === 'string') return step;
          if (step && typeof step === 'object') {
            const time = step.time ? `${step.time} ` : '';
            const title = step.title || '';
            const desc = step.desc ? ` — ${step.desc}` : '';
            return `${time}${title}${desc}`.trim();
          }
          return '';
        }).filter(Boolean);
      }
      return tour.recommendedCourses || [];
    },
    [tour.itinerary, tour.recommendedCourses],
  );
  const tRecommendedCourses = useDeepLTranslated(recommendedCoursesBundle);

  // BE 가 한국어로 내려주는 투어명/차량명/차량 스펙을 현재 UI 언어로 DeepL 번역.
  // i18n key 가 없는 동적 콘텐츠 (어드민이 입력한 임의 문자열) 이라 DeepL fallback 사용.
  const localizableBundle = useMemo(() => {
    const bag = [];
    // BugHerd #214: 투어명(tour.title)은 BE 번역명을 직접 사용 — DeepL 이중번역 제거. bundle 에 미포함.
    if (tour.vehicle) bag.push(tour.vehicle);
    if (tour.surcharge) bag.push(tour.surcharge);
    if (vehicleSpecs.seat) bag.push(vehicleSpecs.seat);
    if (vehicleSpecs.capacity) bag.push(vehicleSpecs.capacity);
    if (vehicleSpecs.luggage) bag.push(vehicleSpecs.luggage);
    return bag;
  }, [tour.vehicle, tour.surcharge, vehicleSpecs]);
  const localizedList = useDeepLTranslated(localizableBundle);
  const tx = useMemo(() => {
    const m = new Map();
    localizableBundle.forEach((src, i) => { m.set(src, localizedList[i] || src); });
    return (s) => (typeof s === 'string' ? (m.get(s) || s) : s);
  }, [localizableBundle, localizedList]);

  /* Form state */
  const [userName, setUserName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [peopleQty, setPeopleQty] = useState(location?.state?.peopleQty || 4);
  // #166: tour 의 minPassengers 가 현재 peopleQty 보다 크면 자동 맞춤.
  useEffect(() => {
    if (!tour.minPassengers) return;
    setPeopleQty((prev) => (prev < tour.minPassengers ? tour.minPassengers : prev));
  }, [tour.minPassengers]);
  const [departureNotes, setDepartureNotes] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);

  /* 로그인 상태면 Guest Info 를 사용자 프로필로 prefill — 이동 서비스 BookerInfoCard 와 동일 패턴 */
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return;
    if (user.name && !userName) setUserName(user.name);
    if (user.phone && !phone) setPhone(user.phone);
    if (user.email && !email) setEmail(user.email);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const [avgRating, setAvgRating] = useState('');
  useEffect(() => {
    reviewService.getStats().then((res) => {
      if (res.average_rating) setAvgRating(res.average_rating);
    }).catch(() => {});
  }, []);

  /* 투어 일정 state — TourDetail에서 전달받은 값으로 초기화. BookingSettings 와 동일하게 name 별도 보관. */
  const [departure, setDeparture] = useState(location?.state?.departure || '');
  const [departureName, setDepartureName] = useState(location?.state?.departureName || '');
  const [arrival, setArrival] = useState(location?.state?.arrival || '');
  const [arrivalName, setArrivalName] = useState(location?.state?.arrivalName || '');
  const [stopovers, setStopovers] = useState(
    (location?.state?.stopovers || []).map((s, i) => ({ ...s, id: s.id ?? Date.now() + i }))
  );
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [editModal, setEditModal] = useState(null); // 'departure' | 'arrival' | 'stopover'
  const [stopoverEditId, setStopoverEditId] = useState(null);

  // #233: 투어 추가 옵션(수량형/체크박스/카시트) — 이동 서비스와 동일 시스템(공용 모듈).
  const { tOptions, optionQty, setOptionQty, optionsTotal, optionInputs } = useServiceOptionList('tour', id);

  // Draft 복원 — mount 시 1회, 같은 history entry(뒤로가기)에 저장된 값이 있으면 폼에 채움.
  useEffect(() => {
    let d;
    try { const raw = sessionStorage.getItem(draftKey); if (raw) d = JSON.parse(raw); } catch {}
    if (!d) return;
    if (d.userName !== undefined) setUserName(d.userName);
    if (d.phone !== undefined) setPhone(d.phone);
    if (d.email !== undefined) setEmail(d.email);
    if (d.peopleQty !== undefined) setPeopleQty(d.peopleQty);
    if (d.departureNotes !== undefined) setDepartureNotes(d.departureNotes);
    if (d.departure !== undefined) setDeparture(d.departure);
    if (d.departureName !== undefined) setDepartureName(d.departureName);
    if (d.arrival !== undefined) setArrival(d.arrival);
    if (d.arrivalName !== undefined) setArrivalName(d.arrivalName);
    if (d.stopovers !== undefined) setStopovers(d.stopovers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draft 저장 — 폼 입력값 변경 시마다 sessionStorage 에 스냅샷.
  useEffect(() => {
    const snap = {
      userName, phone, email, peopleQty, departureNotes,
      departure, departureName, arrival, arrivalName, stopovers,
    };
    try { sessionStorage.setItem(draftKey, JSON.stringify(snap)); } catch {}
  }, [draftKey, userName, phone, email, peopleQty, departureNotes,
      departure, departureName, arrival, arrivalName, stopovers]);

  const addStopover = (index) => {
    const newStop = { id: Date.now(), address: '', name: '' };
    setStopovers((prev) => {
      const next = [...prev];
      next.splice(typeof index === 'number' ? index : prev.length, 0, newStop);
      return next;
    });
    // 신규 row 생성 직후 검색 모달 자동 오픈 (BookingSettings 와 동일 UX).
    setStopoverEditId(newStop.id);
    setEditModal('stopover');
  };
  const removeStopover = (sid) => setStopovers((prev) => prev.filter((s) => s.id !== sid));
  const updateStopover = (sid, address, name) =>
    setStopovers((prev) => prev.map((s) => s.id === sid ? { ...s, address, name: name ?? s.name ?? '' } : s));

  const handleAddressConfirm = (newAddress, newName) => {
    if (editModal === 'departure') {
      setDeparture(newAddress);
      setDepartureName(newName || '');
    } else if (editModal === 'arrival') {
      setArrival(newAddress);
      setArrivalName(newName || '');
    } else if (stopoverEditId) {
      const trimmed = newAddress.trim();
      if (trimmed && (trimmed === departure.trim() || trimmed === arrival.trim())) {
        alert(t('booking.alert_stopover_same'));
        return;
      }
      const otherStopovers = stopovers.filter((s) => s.id !== stopoverEditId);
      if (trimmed && otherStopovers.some((s) => s.address?.trim() === trimmed)) {
        alert(t('booking.alert_stopover_dup'));
        return;
      }
      updateStopover(stopoverEditId, newAddress, newName || '');
    }
    setEditModal(null);
    setStopoverEditId(null);
  };

  // #166: 가격 자동 합산 (TourDetail 과 동일 로직)
  const _ts = tour.dateTime ? new Date(tour.dateTime) : null;
  const _isWeekend = _ts && !Number.isNaN(_ts.getTime()) && (_ts.getDay() === 0 || _ts.getDay() === 6);
  const _hhmm = (tour.dateTime || '').slice(11, 16);
  const _nightStart = (tour.nightStartTime || '18:00').toString().slice(0, 5);
  const _nightEnd = (tour.nightEndTime || '06:00').toString().slice(0, 5);
  const _isNight = _hhmm && (() => {
    if (_nightStart < _nightEnd) return _hhmm >= _nightStart && _hhmm < _nightEnd;
    return _hhmm >= _nightStart || _hhmm < _nightEnd;
  })();
  // #166: 백엔드(tours.py 예약 생성)와 동일 기준 — 기본 포함 인원(min_passengers)까지는 기본가,
  // 초과 인원만 1인당 가산. (예: min=2 → 2명까지 기본, 3명부터 extra_passenger_price 가산)
  const _extraCount = Math.max(0, peopleQty - (tour.minPassengers || 1));
  const totalPrice =
    (tour.basePrice || 0) +
    _extraCount * (tour.extraPassengerPrice || 0) +
    (_isWeekend ? (tour.weekendSurcharge || 0) : 0) +
    (_isNight ? (tour.nightSurcharge || 0) : 0) +
    (optionsTotal || 0); // #233: 추가 옵션 합산

  // #233: 결제페이지(TourPayment) 표시용 선택 옵션 라인. booking→payment 로 넘겨야
  // 결제페이지 "선택 옵션" 섹션과 예상 결제액이 유실되지 않음. optionsTotal 과 동일 기준.
  const selectedOptionItems = (tOptions || []).reduce((acc, opt) => {
    if (opt.variant === 'checkbox') {
      if (optionQty[opt.id]) acc.push({ label: opt.name, value: '', price: `( +${money(opt.price)} )` });
    } else {
      const qty = optionQty[opt.id] || 0;
      const baseQty = opt.baseQty ?? opt.base_qty ?? 0;
      const extra = Math.max(0, qty - baseQty);
      if (extra > 0) acc.push({ label: opt.name, value: `${qty}${opt.unit || ''}`, price: `( +${money(opt.price * extra)} )` });
    }
    return acc;
  }, []);

  const handleSubmit = async () => {
    const available = await checkAvailability();
    if (!available) return;
    if (!userName.trim()) return alert(t('auth.name_placeholder'));
    if (!phone.trim()) return alert(t('auth.phone_placeholder'));
    const tourPhoneDigits = phone.replace(/\D/g, '');
    if (tourPhoneDigits.length < 11) return alert(t('booking.alert_invalid_phone'));
    if (!email.trim()) return alert(t('auth.email_placeholder'));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return alert(t('booking.alert_invalid_email'));

    // 결제 시점에 쿠폰 코드와 함께 한 번에 reservation 생성하기 위한 payload
    const [, pickupTimePart] = (tour.dateTime || '').split('T');
    const stopoverInputs = stopovers
      .filter((s) => s.address && s.address.trim())
      .map((s, i) => ({ address: s.address, name: s.name || null, sort_order: i }));
    const reservationPayload = {
      service_type: 'tour',
      direction: null,
      departure_address: departure,
      departure_name: departureName || null,
      destination_address: arrival,
      destination_name: arrivalName || null,
      destination_region: tour.title || null, // BE pricing: tour name으로 가격 조회
      pickup_date: tour.dateTime || null,
      pickup_time: pickupTimePart || null,
      passenger_count: peopleQty || 4,
      booker_name: userName || null,
      booker_phone: phone || null,
      booker_email: email || null,
      options: optionInputs, // #233: 선택한 추가 옵션 → BE calc_options
      stopovers: stopoverInputs,
      special_requests: departureNotes || null,
      destination_id: Number(id) || null,
      destination_type: 'tour',
    };

    navigate(`/tour/${id}/payment`, {
      state: {
        tour,
        bookingData: { userName, phone, email, peopleQty, departureNotes, stopovers, departure, departureName, arrival, arrivalName, dateTime: tour.dateTime, originalPrice: tour.basePrice, subtotal: totalPrice, optionsTotal, selectedOptionItems, title: tour.title, vehicle: tour.vehicle, baseHours: tour.baseHours, surchargeText: tour.surcharge, reservationPayload },
      },
    });
  };

  return (
    <>
      <Navbar />
      <main className="bg-mono-50 min-h-screen pb-36 sm:pb-32">
        <div className="max-w-form mx-auto px-2.5 sm:px-5 py-5 sm:py-8 flex flex-col gap-1 sm:gap-5">

          {/* ===== Header: Back + Title ===== */}
          <PageHeader title={t('booking.title')} />

          {/* ===== Service info ===== */}
          <Card>
            <div className="space-y-2">
              <Badge variant="service" serviceType="tour">{t('nav.private_tour')}</Badge>
              <h2 className="text-base font-bold text-mono-900">{t('nav.private_tour')} - {tour.title}</h2>
              <p className="text-2sm text-mono-500">{t('payment.scheduled_use_time')}: {tour.dateTime ? tour.dateTime.replace('T', ' ') : '-'}</p>
            </div>
          </Card>

          {/* ===== 차량 정보 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('booking.vehicle_info')} />
            <CarInfoBox
              images={vehicleSpecs.images || []}
              carName={tx(tour.vehicle)}
              usageTimeBadge={t('form.base_time_hours', { hours: tour.baseHours })}
              specs={[
                { icon: 'van', text: tx(vehicleSpecs.seat) || '' },
                { icon: 'person', text: tx(vehicleSpecs.capacity) || '' },
                { icon: 'bag', text: tx(vehicleSpecs.luggage) || '' },
              ]}
            >
              <div className="flex items-center gap-2">
                <Icon name="time" size={18} className="text-mono-500 shrink-0" />
                {/* #269: 차량정보 박스에서 가격 제거(예약조건에 따라 변동→불일치 방지), 이용 시간만 표기 */}
                <span className="text-2sm font-medium text-mono-700">{t('form.usage_time_max', { hours: tour.baseHours })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="time" size={18} className="text-mono-500 shrink-0" />
                <span className="text-2sm font-medium text-mono-700">{tour.surchargeOvertimePrice > 0
                  ? t('booking.overtime_fee_per_hour', { amount: money(tour.surchargeOvertimePrice) })
                  : (tx(tour.surcharge) || '-')}</span>
              </div>
            </CarInfoBox>

            <ReviewButton title={t('transfer_landing.review_title')} rating={avgRating} onClick={() => setShowReviewModal(true)} />
          </Card>

          {/* ===== 예약자 정보 ===== */}
          <BookerInfoCard
            userName={userName} setUserName={setUserName}
            phone={phone} setPhone={setPhone}
            email={email} setEmail={setEmail}
            phoneError={phone.length > 0 && phone.replace(/\D/g, '').length < 11 ? t('booking.alert_invalid_phone') : ''}
          />

          {/* ===== 선택 옵션 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('booking.selected_options')} />
            {/* #166: 차량 정원 6명 상한(모든 투어). 기본 포함 인원(min) 초과분만 가산 — 백엔드와 동일. */}
            <TextBox
              label={t('tour.use_passengers')}
              variant="number"
              value={peopleQty}
              min={tour.minPassengers || 1}
              max={Math.min(tour.maxPassengers || 6, 6)}
              unit={t('common.people')}
              price={tour.extraPassengerPrice || 0}
              baseQty={tour.minPassengers || 1}
              onChange={(v) => setPeopleQty(v)}
            />
            {/* #233: 어드민이 등록한 투어 추가 옵션 (수량형/체크박스/카시트) */}
            {/* #240: 옵션 데이터 이상으로 렌더 예외가 나도 페이지 전체(차량이미지 등)가 죽지 않게 격리. */}
            <ErrorBoundary>
              <ServiceOptionList
                tOptions={tOptions}
                optionQty={optionQty}
                setOptionQty={setOptionQty}
                money={money}
              />
            </ErrorBoundary>
          </Card>

          {/* ===== 투어 일정 ===== */}
          <Card>
            <div className="space-y-5">
              {/* 헤더 */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-5 bg-orange-500 rounded-full shrink-0" />
                  <h3 className="text-base font-bold text-mono-900">{t('tour.schedule_title')}</h3>
                </div>
                <p className="text-sm text-mono-500 ml-3">{t('tour.schedule_desc')}</p>
              </div>

              {/* 추천 코스 */}
              <div className="border border-mono-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                      <Icon name="recommend" size={12} className="text-orange-500" />
                    </div>
                    <span className="text-sm font-medium text-mono-900">{t('tour.recommended_course')}</span>
                  </div>
                  <Icon name={showCourseDropdown ? 'chevron-up' : 'chevron-down'} size={16} className="text-mono-400" />
                </button>
                {showCourseDropdown && (
                  <div className="px-4 py-3 bg-mono-50 border-t border-mono-100">
                    {tRecommendedCourses.length > 0 ? (
                      <ul className="space-y-2">
                        {tRecommendedCourses.map((course, i) => (
                          <li key={i} className="text-sm text-mono-500 leading-relaxed flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0 mt-[7px]" />
                            {course}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-mono-400">{t('tour.no_recommended')}</p>
                    )}
                  </div>
                )}
              </div>

              {/* 1일차 헤더 */}
              <div className="bg-orange-500 rounded-lg px-4 py-2.5">
                <span className="text-sm font-bold text-white">{t('tour.day_1')}</span>
              </div>

              {/* 경로 편집 */}
              <RouteEditSection
                directionLabel={t('tour.movement_route')}
                departure={{ name: departureName, address: departure, time: tour.dateTime ? tour.dateTime.replace('T', ' ') : '' }}
                arrival={{ label: t('form.destination'), name: arrivalName, address: arrival, icon: 'place' }}
                stopovers={stopovers}
                onAddStopover={addStopover}
                onRemoveStopover={removeStopover}
                onEditDeparture={() => setEditModal('departure')}
                onEditArrival={() => setEditModal('arrival')}
                onEditStopover={(sid) => { setStopoverEditId(sid); setEditModal('stopover'); }}
                notes={departureNotes}
                onNotesChange={(e) => setDepartureNotes(e.target.value)}
              />
            </div>
          </Card>

          {/* ===== 현장 추가 요금 안내 — overtime_price 우선해서 useMoney 환산 ===== */}
          <SurchargeNotice
            text={tour.surchargeOvertimePrice > 0
              ? t('booking.overtime_fee_per_hour', { amount: money(tour.surchargeOvertimePrice) })
              : tx(tour.surcharge)}
          />

        </div>
      </main>

      {/* ===== Sticky bottom bar ===== */}
      <StickyBottomBar step={RESERVATION_STEPS.booking} totalSteps={TOTAL_RESERVATION_STEPS}>
        <PriceDisplay label={t('booking.estimated_payment')} amount={totalPrice} size="sm" approx />
        <Button onClick={handleSubmit} width="responsive-130">{t('booking.book_now')}</Button>
      </StickyBottomBar>

      {showReviewModal && <ReviewListModal onClose={() => setShowReviewModal(false)} />}
      {availabilityModal}

      {/* 경로 수정 모달 */}
      {(editModal === 'departure' || editModal === 'arrival' || editModal === 'stopover') && (
        <RouteEditModal
          type="tour"
          field={editModal === 'stopover' ? 'stopover' : editModal}
          currentValue={
            editModal === 'departure' ? departure
            : editModal === 'arrival' ? arrival
            : stopovers.find((s) => s.id === stopoverEditId)?.address || ''
          }
          onConfirm={handleAddressConfirm}
          onClose={() => { setEditModal(null); setStopoverEditId(null); }}
        />
      )}
    </>
  );
}
